import { useEffect, useState, ChangeEvent } from 'react';
import {
  Autocomplete,
  IconDown,
  IconLoader,
  IconTrash,
  IconUp,
  InputNumber,
  InputSize,
  InputText,
  OnSelectChange,
  SelectSize,
  Textarea,
} from '@ui/src';
import { AffilatedCompanyTypes } from './types';
import { AffilatedCompanyFormValues } from '../../../../../hoc/new-application/types';
import { CustomerStatus } from '../../../../../hoc/common/types';
import RegulatedCompanyRow from '../corporate-questionnarie/row';

import cn from '../corporate-questionnarie/styles.module.css';

const AffilatedCompany = ({
  status,
  affilatedCompaniesValues,
  setAffilatedCompaniesValues,
  deleteAffilatedCompanyInProgress,
  onDeleteAffilatedCompany,
  selectedAffilatedCompanyId,
  countries,
  index,
  affilatedCompany,
}: AffilatedCompanyTypes) => {
  const [expanded, setExpanded] = useState(false);
  const [formValues, setFormValues] = useState<AffilatedCompanyFormValues>();

  const onChange = (e: ChangeEvent<HTMLInputElement> | OnSelectChange) => {
    const nextFormValues = { ...formValues };

    const { name, value } = e.target;

    const nextValues = affilatedCompaniesValues?.map((item) => {
      const result = { ...item };

      if (result.id === affilatedCompany?.id) {
        if (name === `name-${affilatedCompany?.id}`) {
          nextFormValues.name = value as string;
          result.name = value as string;
        } else if (name === `registrationNumber-${affilatedCompany?.id}`) {
          nextFormValues.registrationNumber = value as string;
          result.registrationNumber = value as string;
        } else if (name === `mainActivities-${affilatedCompany?.id}`) {
          nextFormValues.mainActivities = value as string;
          result.mainActivities = value as string;
        } else if (name === `position-${affilatedCompany?.id}`) {
          nextFormValues.position = value as string;
          result.position = value as string;
        } else if (name === `countryId-${affilatedCompany?.id}`) {
          nextFormValues.countryId = value as string;
          result.countryId = value as string;
        } else if (name === `percentageOfOwnership-${affilatedCompany?.id}`) {
          nextFormValues.percentageOfOwnership = value as string;
          result.percentageOfOwnership = value as string;
        }
      }

      return result;
    });

    setFormValues(nextFormValues as AffilatedCompanyFormValues);
    setAffilatedCompaniesValues(nextValues as AffilatedCompanyFormValues[]);
  };

  useEffect(() => {
    if (affilatedCompany) {
      setFormValues(affilatedCompany);

      if (affilatedCompany?.id?.includes('affilated')) {
        setExpanded(true);
      }
    }
  }, [affilatedCompany]);

  return (
    <div
      className={`${cn.accountCurrencyWrapper} ${!expanded ? cn.collapsed : ''}`}
    >
      <div
        className={`${cn.accountCurrencyTopWrapper} ${!expanded ? cn.collapsed : ''}`}
      >
        <div className={cn.accountCurrencyTop}>
          <button
            type="button"
            className={cn.accountCurrencyTopLeft}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <IconUp width={14} height={14} />
            ) : (
              <IconDown height={14} width={14} />
            )}
            {`Company #${index + 1}`}
          </button>

          {status !== CustomerStatus.CLOSED && (
            <button
              type="button"
              className={cn.deleteAccountCurrencyButton}
              onClick={() =>
                onDeleteAffilatedCompany(affilatedCompany?.id as string)
              }
            >
              {deleteAffilatedCompanyInProgress &&
              selectedAffilatedCompanyId === affilatedCompany?.id ? (
                <IconLoader width={18} height={18} />
              ) : (
                <IconTrash />
              )}
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className={cn.accountCurrencyContent}>
          <RegulatedCompanyRow
            title="Company name"
            annualIncome
            accountsCurrencies
          >
            <InputText
              name={`name-${affilatedCompany?.id}`}
              placeholder="Enter name..."
              value={formValues?.name}
              size={InputSize.SMALL}
              onChange={(e) =>
                onChange({
                  target: {
                    name: `name-${affilatedCompany?.id}`,
                    value: e.target.value,
                  },
                })
              }
              className="borderless"
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Country" annualIncome accountsCurrencies>
            <Autocomplete
              name={`countryId-${affilatedCompany?.id}`}
              placeholder="Select country..."
              list={countries}
              value={formValues?.countryId}
              size={SelectSize.SMALL}
              onChange={(e) =>
                onChange({
                  target: {
                    name: `countryId-${affilatedCompany?.id}`,
                    value: e.target.value,
                  },
                })
              }
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              borderless
              fullWidth
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow
            title="Registration number"
            annualIncome
            accountsCurrencies
          >
            <InputText
              name={`registrationNumber-${affilatedCompany?.id}`}
              placeholder="Enter number..."
              value={formValues?.registrationNumber}
              size={InputSize.SMALL}
              onChange={(e) =>
                onChange({
                  target: {
                    name: `registrationNumber-${affilatedCompany?.id}`,
                    value: e.target.value,
                  },
                })
              }
              className="borderless"
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow
            title="Main activities"
            textarea
            annualIncome
            accountsCurrencies
          >
            <Textarea
              name={`mainActivities-${affilatedCompany?.id}`}
              placeholder="Enter details......"
              value={formValues?.mainActivities}
              onChange={(e) => {
                onChange({
                  target: {
                    name: `mainActivities-${affilatedCompany?.id}`,
                    value: e.target.value,
                  },
                });
              }}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              disabled={status === CustomerStatus.CLOSED}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow
            title="Position (i.e. director, beneficial owner)"
            annualIncome
            accountsCurrencies
          >
            <InputText
              name={`position-${affilatedCompany?.id}`}
              placeholder="Enter position..."
              value={formValues?.position}
              size={InputSize.SMALL}
              onChange={(e) =>
                onChange({
                  target: {
                    name: `position-${affilatedCompany?.id}`,
                    value: e.target.value,
                  },
                })
              }
              className="borderless"
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow
            title="Percentage of ownership (%)"
            annualIncome
            accountsCurrencies
          >
            <InputNumber
              name={`percentageOfOwnership-${affilatedCompany?.id}`}
              placeholder="Enter percent..."
              value={formValues?.percentageOfOwnership}
              size={InputSize.SMALL}
              onChange={(e) => {
                let nextValue = e.target.value;

                if (Number(e.target.value) < 0) {
                  nextValue = '0';
                } else if (Number(e.target.value) > 100) {
                  nextValue = '100';
                }

                onChange({
                  target: {
                    name: `percentageOfOwnership-${affilatedCompany?.id}`,
                    value: nextValue,
                  },
                });
              }}
              className="borderless"
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
            />
          </RegulatedCompanyRow>
        </div>
      )}
    </div>
  );
};
export default AffilatedCompany;
